import { useEffect, useRef, useState } from 'react';
import { useLocation } from 'wouter';
import { api, DeskError, type NoticeAudience, type NoticeCheck, type TemplateProblem } from '../lib/api';
import type { ConfirmRequest } from '../components/Confirm';
import './knowledge.css';
import './notices.css';

/**
 * The Notices screen (notices-1, Royce 2026-10-21; spec 012 BCST-26 to BCST-34): an email to one audience — or to the
 * account numbers he pastes (named accounts, BCST-37 to BCST-41, every number not resolved listed back). He writes
 * a subject and a plain-text body, picks who, and sees the live count, the rules line and the email as the first
 * recipient will read it. Raise notice creates the template and its broadcast card and opens the card, where he
 * approves it. Nothing sends from here. The desk decides every count, rule and preview; this screen only shows them.
 */

const AUDIENCE_ORDER: NoticeAudience[] = ['in_service', 'paying', 'trials', 'arrears', 'named'];
const AUDIENCE_HINT: Record<NoticeAudience, string> = {
  in_service: 'Active, IOU and IOU (exp)',
  paying: 'In service and ever paid',
  trials: 'In service and never paid',
  arrears: 'An open arrears cycle, Suspended included',
  named: 'Account numbers you paste, in service with an email',
};
const RULE: Record<string, string> = {
  money: 'Money offer', setup: 'Setup offer', placeholder: 'Placeholder', empty: 'Empty', subject_line: 'Subject',
  audience: 'Audience', call_us: 'Phone or hours', fault: 'Fault', activation: 'Activation',
};

const plural = (n: number, one: string, many = `${one}s`) => `${n} ${n === 1 ? one : many}`;

export function Notices({ onConfirm, operator }: { onConfirm: (r: ConfirmRequest) => void; operator: boolean }) {
  const [, navigate] = useLocation();
  const [subject, setSubject] = useState('');
  const [body, setBody] = useState('');
  const [audience, setAudience] = useState<NoticeAudience>('in_service');
  const [accounts, setAccounts] = useState('');
  const [check, setCheck] = useState<NoticeCheck | null>(null);
  const [error, setError] = useState<string | null>(null);
  const seq = useRef(0);

  // The rules line, the counts and the preview, 400 ms after the last keystroke (BCST-27 to BCST-29). Writes nothing.
  useEffect(() => {
    if (!operator) return;
    const n = ++seq.current;
    const t = setTimeout(() => {
      api.notices.check({ subject, body, audience, accounts })
        .then((c) => { if (n === seq.current) { setCheck(c); setError(null); } })
        .catch((e: Error) => { if (n === seq.current) setError(e.message); });
    }, 400);
    return () => clearTimeout(t);
  }, [subject, body, audience, accounts, operator]);

  if (!operator) {
    return <main className="board notices"><p className="list-empty">Notices are the operator&apos;s: only he raises one.</p></main>;
  }

  const count = check?.audiences[audience];
  const refusals = check?.problems ?? [];
  const ready = !!check && !refusals.length && !check.taken && (count?.accounts ?? 0) > 0;
  const named = check?.audiences.named;
  const notSent = named ? named.unknown.length + named.out_of_service.length + named.no_address.length : 0;

  const raise = () => onConfirm({
    title: `Raise a card to send this to ${plural(count?.accounts ?? 0, 'account')}?`,
    detail: `${check?.audiences[audience].label}: ${plural(count?.accounts ?? 0, 'account')}, `
      + `${plural(count?.addresses ?? 0, 'address', 'addresses')}.`
      + `${audience === 'named' && notSent ? ` The ${plural(notSent, 'number')} listed as not resolved will not be sent.` : ''}`
      + ' Nothing is sent until you approve the card.',
    confirmLabel: 'Raise notice',
    onConfirm: async () => {
      try {
        const r = await api.notices.raise({ subject, body, audience, accounts });
        navigate(`/cards/${r.card_id}`);
      } catch (e) {
        const b = (e as DeskError).body as { problems?: TemplateProblem[] } | null;
        setError(b?.problems?.length ? b.problems.map((p) => p.says).join('; ') : (e as Error).message);
      }
    },
  });

  return (
    <main className="board notices">
      <div className="knowledge-head">
        <div>
          <div className="mono eyebrow">Notices</div>
          <h1>Email an audience</h1>
        </div>
      </div>

      <div className="notices-grid">
        <section className="panel notices-form">
          <label className="knowledge-field">
            <span className="mono">Subject</span>
            <input value={subject} maxLength={200} onChange={(e) => setSubject(e.target.value.replace(/[\r\n]/g, ' '))} />
          </label>
          <label className="knowledge-field knowledge-body">
            <span className="mono">Body · plain text · it may use [FIRST_NAME] [NAME] [ACCOUNT]</span>
            <textarea rows={14} value={body} onChange={(e) => setBody(e.target.value)} />
          </label>

          <fieldset className="notices-audiences">
            <legend className="mono">Who</legend>
            {AUDIENCE_ORDER.map((a) => {
              const c = check?.audiences[a];
              return (
                <label key={a} className={`notices-audience ${audience === a ? 'is-current' : ''}`}>
                  <input type="radio" name="audience" checked={audience === a} onChange={() => setAudience(a)} />
                  <span className="notices-audience-name">{c?.label ?? a}<span className="notices-hint">{AUDIENCE_HINT[a]}</span></span>
                  <span className="mono notices-count">
                    {c ? `${plural(c.accounts, 'account')} · ${plural(c.addresses, 'address', 'addresses')}` : '·'}
                  </span>
                </label>
              );
            })}
          </fieldset>

          {audience === 'named' && (
            <div className="notices-named">
              <label className="knowledge-field">
                <span className="mono">Account numbers · one per line or comma-separated</span>
                <textarea rows={6} value={accounts} onChange={(e) => setAccounts(e.target.value)} placeholder={'83280008\n83280030, 83280039'} />
              </label>
              {named && named.pasted.length > 0 && (
                <div className="knowledge-note" role="status">
                  <p className="mono">
                    {named.accounts} of {plural(named.pasted.length, 'number')} resolved · {plural(named.addresses, 'address', 'addresses')}
                    {named.duplicates > 0 && ` · ${plural(named.duplicates, 'duplicate')} removed`}
                  </p>
                  {notSent > 0 && (
                    <ul className="notices-named-out">
                      {named.unknown.length > 0 && <li><span className="mono">Unknown</span> — {named.unknown.join(', ')}</li>}
                      {named.out_of_service.length > 0 && (
                        <li><span className="mono">Out of service</span> — {named.out_of_service.map((o) => `${o.account_no} (${o.status})`).join(', ')}</li>
                      )}
                      {named.no_address.length > 0 && <li><span className="mono">No email address</span> — {named.no_address.join(', ')}</li>}
                      <li className="notices-small">Not on the card and not sent.</li>
                    </ul>
                  )}
                </div>
              )}
            </div>
          )}

          <div className={`knowledge-note template-rules${refusals.length ? ' template-problems' : ''}`} role="status">
            {!check ? 'Checking…'
              : !refusals.length && !check.flags.length ? 'Passes every rule a notice is held to.'
                : <ul>
                  {refusals.map((p, i) => <li key={`r${i}`}><span className="mono">{RULE[p.rule] ?? p.rule}</span> — {p.says}</li>)}
                  {check.flags.map((p, i) => (
                    <li key={`f${i}`} className="notices-flag"><span className="mono">{RULE[p.rule] ?? p.rule}</span> — {p.says}: “{p.words}” (a flag, not a refusal)</li>
                  ))}
                </ul>}
          </div>
          {check?.taken && (
            <p className="knowledge-note template-problems">
              A notice with this subject was raised today: T{check.taken.template_id}
              {check.taken.card_id ? <> · <a href={`/cards/${check.taken.card_id}`}>card {check.taken.card_id}</a>. Reject that card to raise it again.</> : '.'}
            </p>
          )}

          <div className="knowledge-save">
            <button type="button" className="btn-primary" disabled={!ready} onClick={raise}>Raise notice</button>
            <span className="mono notices-small">Raises a card for you to approve. Nothing is sent from this screen.</span>
          </div>
          {error && <p className="list-error">{error}</p>}
        </section>

        <section className="panel notices-preview">
          <h3 className="mono">As the first recipient will receive it</h3>
          {!check?.preview
            ? <p className="list-empty">{!(subject && body) ? 'Write a subject and a body to see it.'
              : audience === 'named' && !named?.pasted.length ? 'Paste the account numbers to see it.'
                : 'Nobody in this audience has an email address.'}</p>
            : <article className="template-preview">
              <h4>{check.preview.name ?? check.preview.account_no} ({check.preview.account_no})</h4>
              <div className="template-subject"><span className="mono">Subject</span> {check.preview.subject}</div>
              <pre className="template-text">{check.preview.text}</pre>
            </article>}
        </section>
      </div>
    </main>
  );
}
